import { useCallback, useEffect, useRef, useState, type FormEvent } from 'react'

import { NearCard, ResultCard } from '@/components/BusinessCard'
import { ListBusinessDialog } from '@/components/ListBusinessDialog'
import { BENEFITS, EXAMPLES, FEATURES, INDUSTRIES, STEPS } from '@/content'
import { GROUP_ICONS, Icon } from '@/icons'
import { searchMarketplace, type MarketplaceResponse, type SearchParams } from '@/lib/api'
import { LINKS } from '@/lib/links'

type Load = { state: 'loading'; data: MarketplaceResponse | null } | { state: 'ok'; data: MarketplaceResponse } | { state: 'error'; data: MarketplaceResponse | null }

/** One live query. A newer one cancels the older, so results never arrive out of order. */
function useMarketplace(params: SearchParams): Load {
  const [load, setLoad] = useState<Load>({ state: 'loading', data: null })
  const key = JSON.stringify(params)

  useEffect(() => {
    const controller = new AbortController()
    setLoad((previous) => ({ state: 'loading', data: previous.data }))
    searchMarketplace(params, controller.signal)
      .then((data) => setLoad({ state: 'ok', data }))
      .catch((error: unknown) => {
        if ((error as Error).name !== 'AbortError') setLoad((previous) => ({ state: 'error', data: previous.data }))
      })
    return () => controller.abort()
    // `key` is `params`, compared by value.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key])

  return load
}

/** The search lives in the address bar, so a result can be shared or reloaded. */
function readUrl() {
  const params = new URLSearchParams(location.search)
  return { q: params.get('q') ?? '', group: params.get('group'), area: params.get('area') }
}

function writeUrl(state: { q: string; group: string | null; area: string | null }) {
  const params = new URLSearchParams()
  if (state.q) params.set('q', state.q)
  if (state.group) params.set('group', state.group)
  if (state.area) params.set('area', state.area)
  const search = params.toString()
  history.replaceState(null, '', `${location.pathname}${search ? `?${search}` : ''}${location.hash}`)
}

const PROMO_SEEN = 'bq_promo'

export function App() {
  const initial = useRef(readUrl()).current
  const [draft, setDraft] = useState(initial.q)
  const [q, setQ] = useState(initial.q)
  const [group, setGroup] = useState<string | null>(initial.group)
  const [area, setArea] = useState<string | null>(initial.area)
  const [menu, setMenu] = useState(false)
  const [promo, setPromo] = useState(false)
  const [example, setExample] = useState(0)
  const [allIndustries, setAllIndustries] = useState(false)

  const search = useMarketplace({ q, group, limit: 12 })
  const near = useMarketplace({ area, limit: 8 })
  const facets = search.data ?? near.data

  useEffect(() => writeUrl({ q, group, area }), [q, group, area])

  useEffect(() => {
    const timer = setInterval(() => setExample((n) => (n + 1) % EXAMPLES.length), 3200)
    return () => clearInterval(timer)
  }, [])

  // The business dialog, once per session. Storage can throw (private mode,
  // blocked site data); then it simply shows each visit.
  useEffect(() => {
    try {
      if (sessionStorage.getItem(PROMO_SEEN)) return
    } catch {
      /* shown anyway */
    }
    const timer = setTimeout(() => setPromo(true), 1000)
    return () => clearTimeout(timer)
  }, [])

  const closePromo = useCallback(() => {
    setPromo(false)
    try {
      sessionStorage.setItem(PROMO_SEEN, '1')
    } catch {
      /* nothing to remember it in */
    }
  }, [])

  const goToResults = () => document.getElementById('discover')?.scrollIntoView()

  const submit = (event?: FormEvent) => {
    event?.preventDefault()
    setQ(draft.trim())
    goToResults()
  }

  const ask = (text: string) => {
    setDraft(text)
    setQ(text)
    setGroup(null)
    goToResults()
  }

  const pickGroup = (id: string) => {
    setGroup((current) => (current === id ? null : id))
    goToResults()
  }

  const clearAll = () => {
    setDraft('')
    setQ('')
    setGroup(null)
  }

  const groupName = facets?.groups.find((entry) => entry.id === group)?.name
  const nothingListed = facets !== null && facets !== undefined && facets.groups.every((entry) => entry.count === 0) && !q && !group

  return (
    <>
      <header className="nav">
        <div className="w">
          <div className="nav-in">
            <a href="#top" className="logo">
              Blooq
            </a>
            <nav className={`links${menu ? ' open' : ''}`} onClick={() => setMenu(false)}>
              <a href="#discover">Discover</a>
              <a href="#categories">Categories</a>
              <a href="#how">How it works</a>
              <a href="#business">For Businesses</a>
              <a className="m" href={LINKS.signIn}>
                Sign in
              </a>
            </nav>
            <div className="right">
              <a className="hide" href={LINKS.signIn}>
                Sign in
              </a>
              <a className="btn sm" href={LINKS.signup}>
                List your business
              </a>
              <button className="burger" aria-label="Menu" aria-expanded={menu} onClick={() => setMenu(!menu)}>
                <i />
                <i />
              </button>
            </div>
          </div>
        </div>
      </header>

      <main>
        <div className="w" id="top">
          <section className="hero">
            <div>
              <h1>Tell Blooq what you need.</h1>
              <p className="sub">Find the right local business, compare your options, and book with confidence.</p>
              <form className="ask" onSubmit={submit}>
                <label htmlFor="ask">
                  <Icon name="sparkles" size={14} /> Ask Blooq
                </label>
                <textarea
                  id="ask"
                  rows={2}
                  maxLength={300}
                  value={draft}
                  placeholder={EXAMPLES[example]}
                  onChange={(event) => setDraft(event.target.value)}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter' && !event.shiftKey) submit(event)
                  }}
                />
                <div className="ask-f">
                  <a href="#categories">Explore businesses</a>
                  <button className="btn g" type="submit">
                    Find what I need
                  </button>
                </div>
              </form>
              <p className="note">Discover local businesses, services and experiences.</p>
            </div>
            <div className="hero-art">
              <img src="/mascot.webp" alt="Blooq mascot pointing the way" />
            </div>
          </section>
        </div>

        <div className="w sec" id="categories">
          <div className="head">
            <h2>Whatever you need, find it on Blooq.</h2>
            <a className="pill" href="#industries">
              Explore all categories
            </a>
          </div>
          <p className="lead">Discover trusted businesses across everyday services, experiences and professional needs.</p>
          <div className="grid g6">
            {(facets?.groups ?? []).map((entry, index) => (
              <button
                key={entry.id}
                type="button"
                className={`tile c${index % 6}${group === entry.id ? ' on' : ''}`}
                aria-pressed={group === entry.id}
                onClick={() => pickGroup(entry.id)}
              >
                <span className="ic">
                  <Icon name={GROUP_ICONS[entry.id] ?? 'store'} size={30} />
                </span>
                <h3>{entry.name}</h3>
                <p>{entry.blurb}</p>
                <span className="n">
                  {entry.count === 0 ? 'Coming soon' : `${entry.count} ${entry.count === 1 ? 'business' : 'businesses'}`}
                </span>
              </button>
            ))}
          </div>
        </div>

        <div className="w sec" id="discover">
          <div className="stage" aria-live="polite" aria-busy={search.state === 'loading'}>
            {q && <div className="bub">{q}</div>}
            <Results
              load={search}
              hasQuery={Boolean(q || group)}
              groupName={groupName ?? null}
              nothingListed={nothingListed}
              onClearGroup={() => setGroup(null)}
              onClearAll={clearAll}
            />
          </div>
        </div>

        <div className="w sec" id="how">
          <div className="head">
            <h2>From need to booking in minutes.</h2>
          </div>
          <ol className="grid g4">
            {STEPS.map(([title, text], index) => (
              <li key={title} className="card step">
                <b>{index + 1}</b>
                <h3>{title}</h3>
                <p className="mute">{text}</p>
              </li>
            ))}
          </ol>
        </div>

        {!nothingListed && (
          <div className="w sec" id="near">
            <div className="head">
              <h2>Discover businesses near you.</h2>
              <span className="mute">Pune</span>
            </div>
            {facets && facets.areas.length > 0 && (
              <div className="chips">
                {facets.areas.map((entry) => (
                  <button
                    key={entry.name}
                    className={`chip${area === entry.name ? ' on' : ''}`}
                    aria-pressed={area === entry.name}
                    onClick={() => setArea(area === entry.name ? null : entry.name)}
                  >
                    {entry.name}
                  </button>
                ))}
              </div>
            )}
            {near.state === 'error' && !near.data ? (
              <p className="mute">Could not load businesses just now.</p>
            ) : (
              <div className="grid g4" aria-busy={near.state === 'loading'}>
                {(near.data?.results ?? []).map((business) => (
                  <NearCard key={business.slug} business={business} />
                ))}
              </div>
            )}
          </div>
        )}

        <div className="w sec" id="business">
          <section className="ban">
            <div>
              <h2>
                Own a business?
                <br />
                Get discovered. Get customers. Manage everything.
              </h2>
              <p>
                Create your free Blooq profile, get discovered by customers searching for your services, accept bookings
                and enquiries, and manage your business from one place.
              </p>
              <div className="acts">
                <a className="btn" href={LINKS.signup}>
                  List your business — Free
                </a>
                <a href="#platform">See how Blooq works for businesses</a>
              </div>
            </div>
            <img src="/mascot.webp" alt="" />
          </section>
          <div className="grid g4" style={{ marginTop: 14 }}>
            {BENEFITS.map(([title, text, tone]) => (
              <a key={title} className={`promo ${tone}`} href={LINKS.signup}>
                <div>
                  <h3>{title}</h3>
                  <p>{text}</p>
                </div>
                <i>
                  <Icon name="arrow" size={16} />
                </i>
              </a>
            ))}
          </div>
        </div>

        <div className="w sec" id="platform">
          <div className="head">
            <h2>Everything your business needs to grow.</h2>
            <a className="pill" href={LINKS.forBusinesses}>
              See it in detail
            </a>
          </div>
          <ul className="grid g3">
            {FEATURES.map(([label, icon]) => (
              <li key={label} className="card feat">
                <span>
                  <Icon name={icon} size={18} />
                </span>
                {label}
              </li>
            ))}
          </ul>
        </div>

        <div className="w sec" id="industries">
          <div className="head">
            <h2>One marketplace. Every kind of business.</h2>
            <button className="pill" onClick={() => setAllIndustries(!allIndustries)}>
              {allIndustries ? 'Show fewer' : 'Explore all industries'}
            </button>
          </div>
          <ul className="chips">
            {(allIndustries ? INDUSTRIES : INDUSTRIES.slice(0, 12)).map((name) => (
              <li key={name}>
                <button className="chip" onClick={() => ask(name)}>
                  {name}
                </button>
              </li>
            ))}
          </ul>
        </div>

        <div className="w sec">
          <section className="final">
            <h2>Whatever you need, start with Blooq.</h2>
            <p>Tell us what you’re looking for. We’ll help you find the right business.</p>
            <a className="btn y lg" href="#top" onClick={() => setTimeout(() => document.getElementById('ask')?.focus(), 300)}>
              Find what I need
            </a>
            <p className="alt">
              <a href={LINKS.signup}>Own a business? List it for free →</a>
            </p>
          </section>
        </div>
      </main>

      <footer>
        <div className="w fg">
          <div>
            <a href="#top" className="logo">
              Blooq
            </a>
            <p className="mute" style={{ marginTop: 8 }}>
              Local discovery and booking, for customers and the businesses they visit.
            </p>
            <p className="mute">Powered by EastLaunch</p>
          </div>
          <div>
            <h4>Explore</h4>
            <a href="#discover">Discover</a>
            <a href="#categories">Categories</a>
            <a href="#how">How it works</a>
          </div>
          <div>
            <h4>For businesses</h4>
            <a href={LINKS.forBusinesses}>Blooq for businesses</a>
            <a href={LINKS.signup}>List your business</a>
            <a href={LINKS.signIn}>Sign in</a>
          </div>
        </div>
      </footer>

      {promo && <ListBusinessDialog onClose={closePromo} />}
    </>
  )
}

function Results({
  load,
  hasQuery,
  groupName,
  nothingListed,
  onClearGroup,
  onClearAll,
}: {
  load: Load
  hasQuery: boolean
  groupName: string | null
  nothingListed: boolean
  onClearGroup: () => void
  onClearAll: () => void
}) {
  const data = load.data

  if (load.state === 'error' && !data) {
    return (
      <div className="empty">
        <h3>We could not reach Blooq just now.</h3>
        <p className="mute">Check your connection and try again.</p>
      </div>
    )
  }

  if (!data) {
    return (
      <div className="row" aria-label="Loading">
        <div className="skel" />
        <div className="skel" />
        <div className="skel" />
      </div>
    )
  }

  if (nothingListed) {
    return (
      <div className="empty">
        <h3>Blooq’s first businesses are joining now.</h3>
        <p className="mute">Own one? Be the first people find here.</p>
        <a className="btn g" href={LINKS.signup}>
          List your business — Free
        </a>
      </div>
    )
  }

  const count = data.total
  const heading = hasQuery
    ? `Blooq found ${count} ${count === 1 ? 'option' : 'options'}`
    : 'Open on Blooq now'

  return (
    <>
      <div className="found">
        <strong>{heading}</strong>
        <div className="got">
          {data.query.understood.map((part) => (
            <span key={part}>{part}</span>
          ))}
          {groupName && (
            <button onClick={onClearGroup} aria-label={`Remove ${groupName} filter`}>
              {groupName} <Icon name="x" size={12} />
            </button>
          )}
        </div>
      </div>

      {data.results.length === 0 ? (
        <div className="empty">
          <h3>No businesses match that yet.</h3>
          <p className="mute">Try fewer words, a different area, or a higher budget.</p>
          <button className="btn" onClick={onClearAll}>
            Show everything on Blooq
          </button>
        </div>
      ) : (
        <>
          <div className="row">
            {data.results.map((business) => (
              <ResultCard key={business.slug} business={business} />
            ))}
          </div>
          <p className="mute" style={{ marginTop: 8 }}>
            Tap a time or View &amp; Book and you land in that business’s own Blooq app to confirm.
          </p>
        </>
      )}
    </>
  )
}
